import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  FileCopy as FileCopyIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import FlexibleTimeInput from "./FlexibleTimeInput";

const WeeklySchedule = ({ shifts = [], onScheduleUpdate, isNoSave = false }) => {
  const [expandedSchedules, setExpandedSchedules] = useState(new Set());
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyModalData, setCopyModalData] = useState({
    sourceDay: "",
    targetDays: [],
    shiftIndex: null,
    shiftSlug: "",
  });
  const [copyModalAnchor, setCopyModalAnchor] = useState(null);
  const [localShifts, setLocalShifts] = useState(shifts);
  const theme = useTheme();

  const toggleScheduleExpand = (index) => {
    setExpandedSchedules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (shifts && shifts.length > 0) {
      setLocalShifts(shifts);
    }
    if (shifts && shifts.length === 0) {
      setLocalShifts([]);
    }
  }, [shifts]);

  const formatTimeRange = (schedule) => {
    if (!schedule) return "";

    if (schedule.start && schedule.end) {
      const startTime = new Date(schedule.start).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      const endTime = new Date(schedule.end).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return (
        <span>
          <strong>{startTime}</strong>
          {" - "}
          <strong>{endTime}</strong>
        </span>
      );
    }

    const scheduledDays = Object.entries(schedule)
      .filter(([_, day]) => day && day.start && day.end)
      .map(([day, times]) => {
        const dayName = day.charAt(0).toUpperCase() + day.slice(1, 3);
        const startTime = new Date(times.start).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        const endTime = new Date(times.end).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        return (
          <Box
            key={day}
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 0.5,
              "&:last-child": {
                mb: 0,
              },
            }}
          >
            <Typography
              sx={{
                minWidth: "45px",
                fontWeight: 600,
                color: "grey",
                fontSize: "14px",
                mr: 1,
              }}
            >
              {dayName}
            </Typography>
            <Typography
              sx={{
                color: "#424242",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <strong>{startTime}</strong>
              <span style={{ color: "#757575" }}>to</span>
              <strong>{endTime}</strong>
            </Typography>
          </Box>
        );
      });

    if (scheduledDays.length === 0) return "";

    return <Box sx={{ display: "flex", flexDirection: "column" }}>{scheduledDays}</Box>;
  };

  const hasInvalidRequiredFields = () => {
    return localShifts.some((shift) => {
      if (!shift.defaultSchedule) return false;

      return Object.entries(shift.defaultSchedule).some(([_, schedule]) => {
        return (schedule.end && !schedule.start) || (schedule.start && !schedule.end);
      });
    });
  };

  const handleTimeChange = (shiftIndex, day, field, newTime) => {
    setLocalShifts((prev) => {
      const newShifts = [...prev];
      if (!newShifts[shiftIndex].defaultSchedule) {
        newShifts[shiftIndex] = {
          ...newShifts[shiftIndex],
          defaultSchedule: {},
          schedule: {},
        };
      }

      // Update both defaultSchedule and schedule
      const newDefaultSchedule = { ...newShifts[shiftIndex].defaultSchedule };
      const newSchedule = { ...newShifts[shiftIndex].schedule };

      if (!newDefaultSchedule[day]) {
        newDefaultSchedule[day] = {};
      }
      if (!newSchedule[day]) {
        newSchedule[day] = {};
      }

      newDefaultSchedule[day][field] = newTime ? newTime.toISOString() : "";
      newSchedule[day][field] = newTime ? newTime.toISOString() : "";

      newShifts[shiftIndex] = {
        ...newShifts[shiftIndex],
        defaultSchedule: newDefaultSchedule,
        schedule: newSchedule,
      };

      if (isNoSave && onScheduleUpdate) {
        onScheduleUpdate(newShifts);
      }

      return newShifts;
    });
  };

  const handleSave = (shiftIndex) => {
    if (onScheduleUpdate) {
      // Ensure both defaultSchedule and schedule are in sync
      const shiftsToSave = localShifts.map((shift) => {
        const defaultSchedule = shift.defaultSchedule || {};
        const schedule = shift.schedule || {};

        // Sync schedule with defaultSchedule
        Object.keys(defaultSchedule).forEach((day) => {
          if (defaultSchedule[day].start || defaultSchedule[day].end) {
            schedule[day] = { ...defaultSchedule[day] };
          }
        });

        return {
          ...shift,
          defaultSchedule,
          schedule,
        };
      });

      onScheduleUpdate(shiftsToSave);
      toggleScheduleExpand(shiftIndex);
    }
  };

  const handleClearSchedule = (shiftIndex, day) => {
    setLocalShifts((prev) => {
      const newShifts = [...prev];
      if (day === "all") {
        newShifts[shiftIndex] = {
          ...newShifts[shiftIndex],
          defaultSchedule: {},
          schedule: {},
        };
      } else if (newShifts[shiftIndex].defaultSchedule) {
        const { [day]: removedDay, ...remainingDays } = newShifts[shiftIndex].defaultSchedule;
        newShifts[shiftIndex] = {
          ...newShifts[shiftIndex],
          defaultSchedule: remainingDays,
        };

        if (newShifts[shiftIndex].schedule) {
          const { [day]: removedScheduleDay, ...remainingScheduleDays } =
            newShifts[shiftIndex].schedule;
          newShifts[shiftIndex].schedule = remainingScheduleDays;
        }
      }

      if (isNoSave && onScheduleUpdate) {
        onScheduleUpdate(newShifts);
      }

      return newShifts;
    });
  };

  const handleCopySchedule = (sourceDay, targetDays, shiftIndexProvided, shiftSlug) => {
    setLocalShifts((prev) => {
      const newShifts = [...prev];
      const shiftIndexFound = newShifts.findIndex((shi) => shi.slug === shiftSlug);
      const shiftIndex = shiftIndexFound ?? shiftIndexProvided;
      const sourceSchedule = newShifts?.[shiftIndex]?.defaultSchedule?.[sourceDay];

      targetDays.forEach((targetDay) => {
        if (!newShifts?.[shiftIndex]?.defaultSchedule) {
          newShifts[shiftIndex].defaultSchedule = {};
        }
        newShifts[shiftIndex].defaultSchedule[targetDay] = { ...sourceSchedule, roster: [] };
      });

      if (isNoSave && onScheduleUpdate) {
        const isValidShifts = hasInvalidRequiredFields();
        onScheduleUpdate(newShifts, isValidShifts);
      }

      return newShifts;
    });
    // setCopyModalOpen(false);
    // setCopyModalAnchor(null);
  };

  return (
    <MDBox>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          fontSize: "18px",
          color: "#212121",
          mb: 2,
        }}
      >
        Weekly Schedule
      </Typography>

      {!localShifts || localShifts.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80px",
            backgroundColor: "#F5F5F5",
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "#757575",
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            No shift selected
          </Typography>
        </Box>
      ) : (
        <Stack spacing={0.5}>
          {localShifts &&
            localShifts.length > 0 &&
            localShifts.map((shift, shiftIndex) => {
              const isExpanded = expandedSchedules.has(shiftIndex);
              const shiftKey = shift.slug || shift.shiftName || `shift-${shiftIndex}`;

              return (
                <Box
                  key={shiftKey}
                  sx={{
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "white",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      minHeight: 85,
                      backgroundColor: "#F5F5F5",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#eeeeee",
                      },
                    }}
                    onClick={() => toggleScheduleExpand(shiftIndex)}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          fontSize: "16px",
                          color: "#212121",
                          mb: 0.5,
                        }}
                      >
                        {shift.shiftName || `Shift ${shiftIndex + 1}`}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#757575",
                          fontSize: "14px",
                        }}
                      >
                        {formatTimeRange(shift.defaultSchedule || shift.schedule)}
                      </Typography>
                    </Box>
                    <IconButton
                      size="medium"
                      sx={{
                        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                        transition: "transform 0.3s ease-in-out",
                        color: "#666",
                        width: 30,
                        height: 30,
                        bottom: isExpanded ? -20 : 0,
                        right: 12,
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Box>

                  <Collapse
                    in={isExpanded}
                    timeout={300}
                    sx={{
                      "& .MuiCollapse-wrapper": {
                        transition: "all 0.3s ease-in-out",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "white",
                        p: 0.5,
                        px: 1,
                        transition: "all 0.3s ease-in-out",
                      }}
                    >
                      <Box sx={{ p: 0, borderRadius: 2 }}>
                        {[
                          "monday",
                          "tuesday",
                          "wednesday",
                          "thursday",
                          "friday",
                          "saturday",
                          "sunday",
                        ].map((day, dayIndex) => {
                          const times = shift.defaultSchedule?.[day] ||
                            shift?.schedule?.[day] || { start: "", end: "" };
                          const dayName = day.charAt(0).toUpperCase() + day.slice(1);

                          return (
                            <Box
                              key={day}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "#fafafa",
                                px: 3,
                                py: 2,
                                mb: 0.5,
                                borderBottom:
                                  dayIndex < 6 ? `1px solid ${theme.palette.grey[100]}` : "none",
                                "&:hover": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 400,
                                  fontSize: "16px",
                                  color: "#424242",
                                  minWidth: 100,
                                  mr: 3,
                                }}
                              >
                                {dayName}
                              </Typography>

                              <Box sx={{ display: "flex", alignItems: "center", mr: 3 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "14px",
                                    color: "#666",
                                    mr: 2,
                                  }}
                                >
                                  From
                                </Typography>
                                <FlexibleTimeInput
                                  value={times.start ? new Date(times.start) : null}
                                  onChange={(newTime) =>
                                    handleTimeChange(shiftIndex, day, "start", newTime)
                                  }
                                  placeholder="--:-- --"
                                  required={!!times.end}
                                  error={!!times.end && !times.start}
                                />
                              </Box>

                              <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "14px",
                                    color: "#666",
                                    mr: 2,
                                  }}
                                >
                                  To
                                </Typography>
                                <FlexibleTimeInput
                                  value={times.end ? new Date(times.end) : null}
                                  onChange={(newTime) =>
                                    handleTimeChange(shiftIndex, day, "end", newTime)
                                  }
                                  placeholder="--:-- --"
                                  required={!!times.start}
                                  error={!!times.start && !times.end}
                                />
                              </Box>

                              <Box
                                sx={{ display: "flex", alignItems: "center", ml: "auto", gap: 1 }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCopyModalAnchor(e.currentTarget);
                                    setCopyModalData({
                                      sourceDay: day,
                                      targetDays: [],
                                      shiftIndex,
                                      shiftSlug: shift.slug,
                                    });
                                    setCopyModalOpen(true);
                                  }}
                                  sx={{
                                    color: "#1976d2",
                                    p: 0.5,
                                  }}
                                >
                                  <FileCopyIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleClearSchedule(shiftIndex, day)}
                                  sx={{
                                    color: "#f44336",
                                    p: 0.5,
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>

                              {dayIndex === 0 && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 4,
                                    right: 12,
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "flex-end",
                                    zIndex: 2,
                                    opacity: isExpanded ? 1 : 0,
                                    transition: "opacity 0.15s ease-out",
                                    pointerEvents: isExpanded ? "auto" : "none",
                                    gap: 1,
                                  }}
                                >
                                  <Button
                                    variant="text"
                                    startIcon={<CloseIcon />}
                                    onClick={() => handleClearSchedule(shiftIndex, "all")}
                                    sx={{
                                      color: "#f44336",
                                      fontSize: "14px",
                                      fontWeight: 500,
                                      textTransform: "none",
                                      mt: 0.5,
                                      "&:hover": {
                                        backgroundColor: "rgba(244, 67, 54, 0.1)",
                                      },
                                    }}
                                  >
                                    Clear All
                                  </Button>
                                  {!isNoSave && (
                                    <Button
                                      variant="contained"
                                      startIcon={<SaveIcon />}
                                      onClick={() => handleSave(shiftIndex)}
                                      disabled={hasInvalidRequiredFields()}
                                      sx={{
                                        backgroundColor: "#1976d2",
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        textTransform: "none",
                                        mt: 0.5,
                                        "&:hover": {
                                          backgroundColor: "#1565c0",
                                        },
                                        "&.Mui-disabled": {
                                          backgroundColor: "#e0e0e0",
                                          color: "#9e9e9e",
                                        },
                                      }}
                                    >
                                      Save
                                    </Button>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
        </Stack>
      )}

      <Popover
        open={copyModalOpen}
        anchorEl={copyModalAnchor}
        onClose={() => {
          setCopyModalOpen(false);
          setCopyModalAnchor(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            minWidth: 220,
            maxWidth: 320,
            background: "#fff",
            p: 0,
            border: "1px solid #e0e0e0",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FileCopyIcon sx={{ mr: 1, color: "#1976d2", fontSize: 18 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "14px" }}>
              Copy to
            </Typography>
          </Box>
          <Stack spacing={0.5} sx={{ mb: 1, ml: 2 }}>
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(
              (day) => {
                const dayKey = day.toLowerCase();
                const isSourceDay = dayKey === copyModalData.sourceDay;
                const isChecked = copyModalData.targetDays.includes(dayKey);
                return (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={isSourceDay ? true : isChecked}
                        disabled={isSourceDay}
                        onChange={(e) => {
                          if (!isSourceDay) {
                            const newTargetDays = e.target.checked
                              ? [...copyModalData.targetDays, dayKey]
                              : copyModalData.targetDays.filter((d) => d !== dayKey);
                            setCopyModalData((prev) => ({ ...prev, targetDays: newTargetDays }));
                            handleCopySchedule(
                              copyModalData.sourceDay,
                              newTargetDays,
                              copyModalData.shiftIndex,
                              copyModalData.shiftSlug
                            );
                          }
                        }}
                        sx={{
                          color: "#1976d2",
                          "&.Mui-checked": { color: "#1976d2" },
                          padding: "4px",
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: isSourceDay ? "#1976d2" : "#212121",
                          fontWeight: isSourceDay ? 600 : 400,
                          ml: 0.5,
                        }}
                      >
                        {day}
                      </Typography>
                    }
                    sx={{
                      margin: 0,
                      minHeight: 32,
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  />
                );
              }
            )}
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={copyModalData.targetDays.length === 6}
                indeterminate={
                  copyModalData.targetDays.length > 0 && copyModalData.targetDays.length < 6
                }
                onChange={(e) => {
                  const allDays = [
                    "monday",
                    "tuesday",
                    "wednesday",
                    "thursday",
                    "friday",
                    "saturday",
                    "sunday",
                  ];
                  const targetDays = e.target.checked
                    ? allDays.filter((day) => day !== copyModalData.sourceDay)
                    : [];
                  setCopyModalData((prev) => ({ ...prev, targetDays }));
                  handleCopySchedule(copyModalData.sourceDay, targetDays, copyModalData.shiftIndex, copyModalData.shiftSlug);
                }}
                sx={{
                  color: "#1976d2",
                  "&.Mui-checked": { color: "#1976d2" },
                  padding: "4px",
                  mr: 1,
                }}
                size="small"
              />
            }
            label={
              <Typography sx={{ fontSize: "14px", color: "#212121", fontWeight: 500 }}>
                Copy to All Days
              </Typography>
            }
            sx={{
              margin: 0,
              minHeight: 32,
              width: "100%",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              pl: 1,
              mt: 0.5,
              mb: 0.5,
            }}
            onClick={() => {
              setCopyModalData((prev) => ({ ...prev, targetDays: [] }));
              handleClearSchedule(copyModalData.shiftIndex, "all");
              setCopyModalOpen(false);
              setCopyModalAnchor(null);
            }}
          >
            <CloseIcon sx={{ color: "#f44336", fontSize: 20, mr: 1 }} />
            <Typography sx={{ color: "#f44336", fontSize: "14px", fontWeight: 500 }}>
              Clear
            </Typography>
          </Box>
        </Box>
      </Popover>
    </MDBox>
  );
};

export default WeeklySchedule;
